﻿import {
  AlertCircle,
  ChevronRight,
  FileText,
  Image as ImageIcon,
  Info,
  Loader2,
  Percent,
  Wallet,
} from "lucide-react";
import { useT } from "@/lib/i18n";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { DashboardPage } from "@/components/dashboard-page";
import { ImageUploader } from "@/components/image-uploader";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useMeQuery, useUpdateMeMutation } from "@/features/organizers/api";
import { LegalInfoForm } from "@/features/organizers/legal-info-form";
import { ApiError } from "@/lib/api-client";
import { formatCurrency } from "@ticketnjoy/types";

/**
 * Ayarlar sayfası — v1: organizer profil + platform koşulları (komisyon, servis
 * ücreti) read-only. İleride profil düzenleme, fatura bilgileri, webhook'lar
 * buraya gelecek.
 */
export function SettingsPage() {
  return (
    <DashboardPage>
      <SettingsView />
    </DashboardPage>
  );
}

function SettingsView() {
  const t = useT();
  const { data, isLoading, error } = useMeQuery();
  const updateMe = useUpdateMeMutation();

  async function handleLogoChange(url: string | null) {
    try {
      await updateMe.mutateAsync({ logoUrl: url });
      toast.success(t("common.save"));
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.detail : t("common.unexpectedError"),
      );
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader className="items-center text-center">
          <AlertCircle className="h-10 w-10 text-destructive" />
          <CardTitle>{t("settings.loadError")}</CardTitle>
          <CardDescription>{t("settings.loadErrorDetail")}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("settings.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("settings.subtitle")}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Sol sütun: profil (logo, email) + platform koşulları.
            Sağ sütun full-width üzerinde Yasal Bilgiler — daha geniş bir form
            alanı gerekiyor (adres textarea'sı için özellikle). */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t("settings.profile")}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <Row label={t("settings.company")} value={data.companyName} />
            <Row label={t("settings.email")} value={data.email} />
            <Row
              label={t("settings.preferredLocale")}
              value={data.preferredLocale.toUpperCase()}
            />
            <div className="space-y-2 pt-2">
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <ImageIcon className="h-3.5 w-3.5" />
                {t("settings.logo")}
              </div>
              <ImageUploader
                kind="OrganizerLogo"
                aspect="square"
                actionsPosition="beside"
                value={data.logoUrl}
                onChange={handleLogoChange}
                disabled={updateMe.isPending}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Percent className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">
                {t("settings.platformTerms")}
              </CardTitle>
            </div>
            <CardDescription className="flex items-start gap-1.5 text-xs">
              <Info className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{t("settings.termsReadOnly")}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Metric
              icon={<Percent className="h-4 w-4" />}
              label={t("settings.commissionRate")}
              value={`%${data.commissionRate}`}
              description={t("settings.commissionRateDesc")}
            />
            <Metric
              icon={<Wallet className="h-4 w-4" />}
              label={t("settings.serviceFee")}
              value={`${formatCurrency(data.serviceFee, "TRY")} / ${t("settings.perTicket")}`}
              description={t("settings.serviceFeeDesc")}
            />
          </CardContent>
        </Card>
      </div>

      {/* Yasal bilgiler full width: uzun form + adres textarea için tek sütun
          daha okunaklı. Shop aktivasyonu bu formun tamamlanmasına bağlı. */}
      <LegalInfoForm organizer={data} />

      {/* Yasal sayfa override editörü — ayrı route, tab'lı 5 sayfa editörü. */}
      <Link
        to="/settings/legal"
        className="group block rounded-lg border bg-card transition hover:border-primary/40 hover:shadow-sm"
      >
        <div className="flex items-center justify-between gap-4 p-5">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-accent p-2 text-accent-foreground">
              <FileText className="h-4 w-4" />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-semibold">
                {t("settings.legalOverrides.title")}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("settings.legalOverrides.subtitle")}
              </p>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-foreground" />
        </div>
      </Link>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function Metric({
  icon,
  label,
  value,
  description,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  description: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          <span className="text-muted-foreground">{icon}</span>
          {label}
        </div>
        <span className="text-base font-semibold tabular-nums">{value}</span>
      </div>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  );
}
